import "jquery-ui";
import { _ } from "../../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import jQuery from "jquery";

const delay = (function () {
	let timer = 0;
	return function (callback, ms) {
		clearTimeout(timer);
		timer = setTimeout(callback, ms);
	};
})();

/**
 * `SearchFieldWidget` is a widget used to filter another widget entries.
 * The basic use case is to apply it search on a gridWidget/cardListWidget.
 *
 * The queries are not sent at each keystroke but wait for `timeout` milliseconds
 * without a keystroke to trigger a new query.
 *
 * @param {boolean} mobileOnly - Due the header structure, two
 * searchFields must be displayed, one for mobile, one not.
 * @param {string} [placeholder] - Placeholder string to display
 * when the searchFiled content is empty.
 * @param {Integer} timeout - Time in ms to wait after a
 * keystroke before the query is actually triggered.
 * @param {boolean} [onlySearchOnEnter] - Set if a new query must
 * be triggered only when [Enter] is pressed.
 * @param {string} [inputId] - DOM "id" of the generated text
 * INPUT field.
 * @param {string} [inputClass] - DOM "class" of the generated text
 * INPUT field.
 */
export default class SearchFieldWidget extends Widget {
	constructor({
		mobileOnly,
		placeholder = _("Search…"),
		timeout = 600,
		onlySearchOnEnter,
		inputId = `searchfield${mobileOnly ? "-mobile" : ""}`,
		inputClass = "pull-right form-control search-field",
	} = {}) {
		super(...arguments);

		this._mobileOnly = mobileOnly;
		this._placeholder = placeholder;
		this._timeout = timeout;
		this._onlySearchOnEnter = onlySearchOnEnter;

		this._inputId = inputId;
		this._inputClass = inputClass;

		this._hidden = true;
		this._listening = true;
		this._searchString = "";
		this._onResume = undefined;

		this._onSearchChange = this._events.createEvent();

		this.applySearchOnOldAbstractListWidget = this.applySearchOnGrid;
		this.applySearchOnOldListSwitcher = this.applySearchOnGrid;
	}

	setSearchString(string) {
		this._searchString = string;
		this._onSearchChange.trigger(string);
		this.update();
	}

	reset() {
		this._searchString = "";
		this.update();
	}

	toggleSearchFieldVisibility() {
		const target = this.asJQuery().find("input");

		if (this._hidden) {
			target.show("slide", { direction: "right" });
			target.focus();
		} else {
			target.hide("slide", { direction: "right" });
			target.blur();
		}

		this._hidden = !this._hidden;
	}

	applySearchOn(searchableWidget) {
		searchableWidget.applySearchFrom(this);
	}

	applySearchOnList(categorizedList) {
		this._onSearchChange.register(() => {
			this._stop();
			categorizedList.updateSearch(this._searchString);
			this._start();
		});
	}

	applySearchOnTree(tree) {
		this._onSearchChange.register(() => {
			this._stop();
			tree.filterNodes(this._searchString);
			this._start();
		});
	}

	applySearchOnGrid(grid) {
		this._onSearchChange.register(() => {
			this._stop();
			grid.searchBy(this._searchString);
		});

		grid.onData.register(() => {
			this._start();
		});
	}

	applySearchOnListSource(listSource) {
		this._onSearchChange.register(() => {
			this._stop();
			listSource.searchBy(this._searchString);
			listSource.fetch();
		});

		listSource.registerOnDataFetch(({ loading }) => {
			if (loading) return;
			this._start();
		});
	}

	renderContentOn(html) {
		const input = html.input({
			value: this._searchString,
			id: this._inputId,
			type: "text",
			class: this._inputClass,
			placeholder: this._placeholder,
			name: "search",
		});

		input.keyup((event) => {
			const keyCode = event.keyCode || event.which;

			if (this._onlySearchOnEnter && keyCode !== 13) {
				return;
			}

			// Ignore arrow-keys
			if (keyCode >= 37 && keyCode <= 40) {
				return;
			}

			// Use jQuery since it's embedded in a popover
			// which use the html string to re create its content.
			// Therefore the value of input is not was is rendered on screen
			this._searchString = jQuery(`#${this._inputId}`).val();

			delay(() => {
				if (this._listening) {
					this._onSearchChange.trigger(this._searchString);
				} else {
					this._onResume = () => {
						this._onSearchChange.trigger(this._searchString);
					};
				}
			}, this._timeout);
		});
	}

	_stop() {
		this._listening = false;
	}

	_start() {
		this._listening = true;
		if (this._onResume) {
			this._onResume();
			this._onResume = undefined;
		}
	}
}
